import {announce} from '@github-ui/aria-live'
import {type RefObject, useCallback, useEffect, useLayoutEffect, useRef} from 'react'

import type {TimelineItem} from './IssueTimeline'
import {
  decideTimelineFocus,
  isTimelineLoadComplete,
  LOAD_CONTROL_SELECTOR,
  loadControlSelector,
  resolveBaseItemFocusTarget,
  resolveTimelineFocusTarget,
} from './timeline-focus-target'

type PendingFocus = {
  baseId: string | undefined
  isComment: boolean
  position: 'top' | 'bottom'
  isLoadAll: boolean
  itemCountAtRequest: number
}

// Tracks a pending load's announcement independently of focus (see the layout effect), so
// giving up focus mid-load never suppresses the final count.
type PendingAnnounce = {
  isLoadAll: boolean
  position: 'top' | 'bottom'
  eventCountAtRequest: number
}

// Counts the rendered event rows (each `event` item is one row; a rolled-up group is a
// single row). `load` pseudo-rows are excluded so the announcement counts real events.
const countEventRows = (items: TimelineItem[]): number =>
  items.reduce((total, item) => total + (item && item.type === 'event' ? 1 : 0), 0)

// Screen-reader announcement after a user-initiated "Load more"/"Load all": how many new
// event rows arrived. Deliberately no running total — under list semantics the "Load more"
// control is itself an `<li>`, so a "showing N" figure would disagree with the item count a
// screen reader derives from the `<ol>` while a control remains.
const buildLoadAnnouncement = (newCount: number): string =>
  `${newCount} new ${newCount === 1 ? 'item' : 'items'} loaded.`

// The announcement is delayed so the screen reader speaks the focus move into the newly
// loaded rows first, then the count; a polite announcement made in the same tick as the
// focus change is dropped by most screen readers. Matches the delayed-announce pattern used
// elsewhere for focus + live-region coexistence (e.g. custom-roles PermissionsPicker).
export const TIMELINE_LOAD_ANNOUNCE_DELAY_MS = 1000

/**
 * Manages keyboard focus and a screen-reader announcement after a timeline "Load more"/"Load
 * all" control is activated.
 *
 * `requestFocusAfterLoad` is called synchronously when the load is triggered; a layout effect
 * then runs after every timeline commit and reads the DOM. Focus defers to `decideTimelineFocus`
 * (a pure, unit-tested matrix) — focus moves to the newly-loaded content (its heading), falling
 * back to the base item so focus is never stranded on `<body>`; the matrix only decides *when*
 * it is safe to do so (the control unmounting for "Load all", the rows committing for a partial
 * "Load more"). A sibling control (top vs bottom, when deep-linked to a comment) can coexist, so
 * the control lookup is scoped to the activated position.
 *
 * The announcement runs on its own track (`isTimelineLoadComplete`), not on the focus decision:
 * the user may move focus away mid-load (focus is given up), but a screen-reader user still needs
 * the final count, so the announcement waits for the load to fully land (the "Load all" control
 * disappearing, or a "Load more" chunk committing) and reports the total new rows.
 */
export function useFocusAfterTimelineLoad(
  timelineItems: TimelineItem[],
  timelineRef: RefObject<HTMLElement | null>,
): (baseId: string | undefined, isComment: boolean, position: 'top' | 'bottom', isLoadAll: boolean) => void {
  const pendingRef = useRef<PendingFocus | null>(null)
  const announcePendingRef = useRef<PendingAnnounce | null>(null)
  // Last committed item count, read at request time so the partial-load branch can
  // tell when new rows have landed (and the page has remounted the control).
  const itemCountRef = useRef(timelineItems.length)
  // Rendered event-row count, seeded by the layout effect below (which runs on mount before
  // any load can be requested) so the announcement can report how many new rows a load added.
  const eventCountRef = useRef(0)
  // Holds the pending delayed announcement so a rapid second load or an unmount can cancel it.
  const announceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const requestFocusAfterLoad = useCallback(
    (baseId: string | undefined, isComment: boolean, position: 'top' | 'bottom', isLoadAll: boolean) => {
      pendingRef.current = {baseId, isComment, position, isLoadAll, itemCountAtRequest: itemCountRef.current}
      announcePendingRef.current = {isLoadAll, position, eventCountAtRequest: eventCountRef.current}
    },
    [],
  )

  useLayoutEffect(() => {
    const itemCountAtCommit = timelineItems.length
    itemCountRef.current = itemCountAtCommit
    const eventCountAtCommit = countEventRows(timelineItems)
    eventCountRef.current = eventCountAtCommit

    const timeline = timelineRef.current
    if (!timeline) return

    const pending = pendingRef.current
    if (pending) {
      const active = document.activeElement
      const control = timeline.querySelector<HTMLElement>(loadControlSelector(pending.position))

      const decision = decideTimelineFocus({
        isLoadAll: pending.isLoadAll,
        controlPresent: control !== null,
        itemsGrew: itemCountAtCommit > pending.itemCountAtRequest,
        focusEscaped:
          active !== null &&
          active !== document.body &&
          (!timeline.contains(active) || !active.closest(LOAD_CONTROL_SELECTOR)),
      })

      if (decision !== 'wait') {
        if (decision === 'focus-content') {
          const target =
            resolveTimelineFocusTarget(pending.baseId, pending.isComment, timeline) ??
            resolveBaseItemFocusTarget(pending.baseId, timeline)
          target?.focus({preventScroll: true})
        }
        // 'abort' and 'focus-content' both consume the focus request.
        pendingRef.current = null
      }
    }

    // Announce independently of focus: fire once the load has fully landed (see
    // isTimelineLoadComplete), deferred past the focus move so it is not dropped (see
    // TIMELINE_LOAD_ANNOUNCE_DELAY_MS). Live and optimistic inserts set no request, so they
    // stay silent.
    const announcePending = announcePendingRef.current
    if (announcePending) {
      const controlPresent = timeline.querySelector<HTMLElement>(loadControlSelector(announcePending.position)) !== null
      const complete = isTimelineLoadComplete({
        isLoadAll: announcePending.isLoadAll,
        controlPresent,
        eventsGrew: eventCountAtCommit > announcePending.eventCountAtRequest,
      })
      if (complete) {
        const newEventCount = eventCountAtCommit - announcePending.eventCountAtRequest
        if (newEventCount > 0) {
          const message = buildLoadAnnouncement(newEventCount)
          if (announceTimeoutRef.current !== null) clearTimeout(announceTimeoutRef.current)
          announceTimeoutRef.current = setTimeout(() => {
            announce(message)
            announceTimeoutRef.current = null
          }, TIMELINE_LOAD_ANNOUNCE_DELAY_MS)
        }
        announcePendingRef.current = null
      }
    }
  }, [timelineItems, timelineRef])

  // Cancel a pending delayed announcement if the timeline unmounts before it fires.
  useEffect(
    () => () => {
      if (announceTimeoutRef.current !== null) clearTimeout(announceTimeoutRef.current)
    },
    [],
  )

  return requestFocusAfterLoad
}
